/**
 * Browser + device fingerprint sent along with every QR resolution.
 *
 * Kept deliberately close to the original Ecollat helper so the deviceid /
 * devicename values recorded here line up with existing analytics. Three
 * fixes, documented in the README:
 *   1. the devicename fallback was unreachable and could stringify "undefined"
 *   2. Opera / Samsung / UC all advertise "Chrome/" and were reported as Chrome
 *   3. iPadOS 13+ reports "Macintosh"; maxTouchPoints tells the two apart
 *
 * navigator and screen are parameters rather than globals so this can be
 * tested against real user-agent strings without a DOM.
 */

// Most specific first: every Chromium fork below also matches /Chrome\//.
const BROWSERS = [
  ['Mozilla Firefox', /Firefox\//],
  ['Microsoft Edge', /Edg\//],
  ['Opera', /OPR\/|Opera\//],
  ['Samsung Internet', /SamsungBrowser\//],
  ['UC Browser', /UCBrowser\//],
  ['QQ Browser', /MQQBrowser\//],
  ['WeChat Browser', /MicroMessenger\/|WeChat\//],
  ['Yandex Browser', /YaBrowser\/|Yandex\//],
  ['Google Chrome', /Chrome\//],
  ['Apple Safari', /Safari\//],
  ['Microsoft Internet Explorer', /Trident\//],
  ['WebKit-based Browser (unknown)', /AppleWebKit\//],
];

function detectDevice(ua, maxTouchPoints) {
  // An iPad has claimed to be a Macintosh since iPadOS 13; only the touch
  // point count separates the two.
  const isTouchMac = /Macintosh/.test(ua) && maxTouchPoints > 1;

  const device = {
    iPad: /iPad/.test(ua) || isTouchMac,
    iPhone: /iPhone/.test(ua),
    iPod: /iPod/.test(ua),
    Android: /Android/.test(ua),
    Windows: /Windows/.test(ua),
    macOS: /Macintosh/.test(ua) && !isTouchMac,
    Linux: /Linux/.test(ua),
    BlackBerry: /BlackBerry/.test(ua),
    Samsung: /Samsung/.test(ua),
    Nokia: /Nokia/.test(ua),
    Sony: /Sony/.test(ua),
    HTC: /HTC/.test(ua),
    LG: /LG/.test(ua),
    Motorola: /Motorola/.test(ua),
    Nexus: /Nexus/.test(ua),
    Kindle: /Kindle/.test(ua),
    FirePhone: /Fire Phone/.test(ua),
  };

  return Object.keys(device).find((name) => device[name]) ?? '';
}

export function detectBrowserAndDevice({
  navigator: nav = typeof window === 'undefined' ? {} : window.navigator,
  screen: screenInfo = typeof window === 'undefined' ? {} : window.screen,
  origin = typeof window === 'undefined' ? '' : window.location.origin,
} = {}) {
  const ua = nav?.userAgent ?? '';

  const browserName = BROWSERS.find(([, re]) => re.test(ua))?.[0] ?? 'Unknown Browser';
  const deviceType = detectDevice(ua, nav?.maxTouchPoints ?? 0);

  const uid = [
    nav?.mimeTypes?.length ?? 0,
    ua.replace(/\D+/g, ''),
    nav?.plugins?.length ?? 0,
    screenInfo?.height ?? '',
    screenInfo?.width ?? '',
    screenInfo?.pixelDepth ?? '',
  ].join('');

  return {
    devicename: `${browserName}${deviceType}` || 'Unknown Device',
    deviceid: uid || 'unknown',
    domain: origin,
  };
}
